import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Upload, FileSpreadsheet, FileText, AlertCircle, X, Plus } from 'lucide-react';
import { fnoReconApi } from '@/shared/api';

/** Multi-file upload zone (mirrors the Recon new-job page). */
function MultiFileZone({
  label, hint, accept, icon: Icon, files, onChange, max,
}: {
  label: string;
  hint: (count: number) => string;
  accept: string;
  icon: React.ElementType;
  files: File[];
  onChange: (files: File[]) => void;
  max: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-axis-ink">{label}</span>
      {files.length < max && (
        <label className="flex items-center gap-3 p-3.5 border-2 border-dashed border-axis-cloud-200 rounded-md hover:border-axis-maroon cursor-pointer">
          {files.length > 0 ? <Plus className="w-5 h-5 text-axis-maroon shrink-0" /> : <Icon className="w-5 h-5 text-axis-maroon shrink-0" />}
          <span className="text-xs text-axis-ash leading-snug">{hint(files.length)}</span>
          <input type="file" multiple accept={accept} className="hidden"
            onChange={e => {
              const inc = Array.from(e.target.files ?? []);
              onChange([...files, ...inc].slice(0, max));
            }} />
        </label>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-axis-cloud-50 border border-axis-cloud-200 rounded-full max-w-[220px]">
              <Icon className="w-3.5 h-3.5 text-axis-maroon shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{f.name}</div>
                <div className="text-[10px] text-axis-ash">{(f.size / 1024).toFixed(1)} KB</div>
              </div>
              <button type="button"
                onClick={() => onChange(files.filter((_, idx) => idx !== i))}
                className="ml-0.5 shrink-0 rounded-full hover:bg-axis-cloud-200 text-axis-ash hover:text-rose-500 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {files.length === max && (
            <div className="text-[11px] text-axis-ash self-center">Maximum of {max} files reached.</div>
          )}
        </div>
      )}
    </div>
  );
}

export function NewFnoReconPage() {
  const nav = useNavigate();
  const [portfolioFiles, setPortfolioFiles] = useState<File[]>([]);
  const [dpFiles, setDpFiles] = useState<File[]>([]);

  const mutation = useMutation({
    mutationFn: fnoReconApi.create,
    onSuccess: (data) => nav(`/fno-recon/${data.job_id}`),
  });

  const ready = portfolioFiles.length > 0 && dpFiles.length > 0;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-semibold text-axis-ink mb-1">New F&amp;O reconciliation</h1>
      <p className="text-sm text-axis-ash mb-6">
        Upload a portfolio F&amp;O file and the BO trade book.
      </p>

      <div className="card p-6 space-y-6">
        <MultiFileZone
          label="Portfolio file(s) (.xlsx) — up to 4 files"
          hint={n => n === 0 ? 'Click to select up to 4 .xlsx files' : `Add more files (${n}/4 selected)`}
          accept=".xlsx,.xls"
          icon={FileSpreadsheet}
          files={portfolioFiles}
          onChange={setPortfolioFiles}
          max={4}
        />

        <MultiFileZone
          label="BO Trade Book (.xlsx / .csv)"
          hint={n => n === 0 ? 'Click to select the BO trade book file(s)' : `Add more BO files (${n} selected)`}
          accept=".pdf,.csv,.xlsx,.xls"
          icon={FileText}
          files={dpFiles}
          onChange={setDpFiles}
          max={99}
        />

        {mutation.isError && (
          <div className="flex gap-2 p-3 bg-rose-500/10 border border-rose-400/30 rounded-md text-rose-300 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <div>{(mutation.error as Error).message}</div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-axis-cloud-200">
          <button type="button" className="btn-secondary" onClick={() => nav('/fno-recon')}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!ready || mutation.isPending}
            onClick={() => {
              mutation.mutate({ portfolio_files: portfolioFiles, dp_files: dpFiles });
            }}
          >
            <Upload className="w-4 h-4" />
            {mutation.isPending ? 'Uploading…' : 'Start reconciliation'}
          </button>
        </div>
      </div>
    </div>
  );
}
